import { Link } from 'react-router-dom';
import { useAuth } from '@/features/auth/auth.store';
import { useDashboardSummary } from '@/features/dashboard/dashboard.api';

interface StatCardProps {
  label: string;
  value: number;
  hint?: string;
  tone?: 'default' | 'open' | 'good' | 'warn';
  to?: string;
}

function StatCard({ label, value, hint, tone = 'default', to }: StatCardProps) {
  const toneClasses: Record<NonNullable<StatCardProps['tone']>, string> = {
    default: 'border-slate-200 bg-white',
    open: 'border-amber-200 bg-amber-50',
    good: 'border-emerald-200 bg-emerald-50',
    warn: 'border-rose-200 bg-rose-50',
  };
  const valueClasses: Record<NonNullable<StatCardProps['tone']>, string> = {
    default: 'text-slate-900',
    open: 'text-amber-700',
    good: 'text-emerald-700',
    warn: 'text-rose-700',
  };
  const inner = (
    <>
      <div className={`text-3xl font-bold tabular-nums ${valueClasses[tone]}`}>{value}</div>
      <div className="mt-1 text-sm font-medium text-slate-700">{label}</div>
      {hint ? <div className="mt-0.5 text-xs text-slate-500">{hint}</div> : null}
    </>
  );
  const base = `rounded-xl border p-5 ${toneClasses[tone]}`;
  if (to) {
    return (
      <Link
        to={to}
        className={`${base} block transition hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-slate-400`}
      >
        {inner}
      </Link>
    );
  }
  return <div className={base}>{inner}</div>;
}

export function DashboardPage() {
  const user = useAuth((s) => s.user);
  const { data, isLoading, isError, refetch } = useDashboardSummary();

  return (
    <div>
      <h1 className="text-xl font-bold text-slate-900">Dashboard</h1>
      <p className="mt-1 text-sm text-slate-500">
        Angemeldet als {user?.email} ({user?.role})
      </p>

      {isLoading ? (
        <div className="mt-6 rounded-lg border border-slate-200 p-8 text-center text-slate-400">
          Kennzahlen werden geladen …
        </div>
      ) : isError || !data ? (
        <div className="mt-6 rounded-lg border border-rose-200 bg-rose-50 p-6 text-center">
          <p className="text-sm text-rose-700">Kennzahlen konnten nicht geladen werden.</p>
          <button
            onClick={() => refetch()}
            className="mt-3 rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700"
          >
            Erneut versuchen
          </button>
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Zusatzleistungen
            </h2>
            <p className="mt-0.5 text-xs text-slate-400">
              Dokumentieren → Bestätigen → Abrechnen
            </p>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              <StatCard
                label="Offen (Büro)"
                value={data.extraServices.openInternal}
                hint="Interne Prüfung ausstehend"
                tone="open"
                to="/zusatzleistungen?tab=open"
              />
              <StatCard
                label="Bereit zum Senden"
                value={data.extraServices.confirmedNotSent}
                hint="Intern bestätigt, noch nicht beim Kunden"
              />
              <StatCard
                label="Beim Kunden"
                value={data.extraServices.atCustomer}
                hint="Entscheidung ausstehend"
              />
              <StatCard
                label="Kunde bestätigt"
                value={data.extraServices.customerConfirmed}
                hint="Abrechenbar"
                tone="good"
                to="/zusatzleistungen?tab=confirmed"
              />
              <StatCard
                label="Kunde abgelehnt"
                value={data.extraServices.customerRejected}
                tone={data.extraServices.customerRejected > 0 ? 'warn' : 'default'}
              />
            </div>
            <p className="mt-3 text-xs text-slate-400">
              {data.extraServices.total} Zusatzleistungen gesamt
            </p>
          </section>

          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Kundenfreigaben
            </h2>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
              <StatCard label="Offen" value={data.approvals.pending} tone="open" />
              <StatCard label="Bestätigt" value={data.approvals.confirmed} tone="good" />
              <StatCard
                label="Abgelehnt"
                value={data.approvals.rejected}
                tone={data.approvals.rejected > 0 ? 'warn' : 'default'}
              />
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
